import { useEffect, useMemo } from 'react';
import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import { AnimatePresence } from 'framer-motion';
import { Check, Plus } from 'lucide-react';

import { Badge } from 'components/Badge';
import { Button } from 'components/Button';
import { Label } from 'components/Label';
import { MultiSelect } from 'components/MultiSelect';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  LTLShiplifyDecision,
  LTLShiplifyLookupMetadata,
} from 'lib/api/getLTLQuickQuotes';
import {
  LTLAccessorial,
  LTLAccessorialCategoryTag,
  LTLSelectedAccessorial,
  LTL_UNIT_SYSTEMS,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { StopTypes } from 'types/Appointment';
import { Undef } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

import { AccessorialCard } from '../AccessorialCard';
import {
  CATEGORY_CONFIG,
  CATEGORY_ORDER,
  findAccessorialById,
  getAccessorialId,
  getCountsByCategory,
  getPrimaryCategory,
  sortSelectedAccessorials,
} from '../accessorialUtils';
import { SHIPLIFY_STOP_TYPES } from '../shiplifyConsts';
import {
  ShiplifyRecommendationMatch,
  formatShiplifyLocationType,
  formatShiplifyTariffCode,
  getShiplifyAddressKey,
  getShiplifyStop,
  matchShiplifyRecommendations,
  shiplifyConfidenceAccuracy,
} from '../shiplifyLookup';
import {
  getTaiOverlengthAccessorialCode,
  reconcileTaiOverlengthAccessorials,
} from './overlengthAccessorial';

interface AccessorialsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  accessorials: LTLAccessorial[];
  isLoading?: boolean;
}

type ShiplifyRecommendationDisplay = ShiplifyRecommendationMatch & {
  stopType: StopTypes;
  category: LTLAccessorialCategoryTag;
  originalIndex: number;
};

interface ShiplifyRecommendationData {
  accuracy: Undef<number>;
  exempt: boolean;
  locationTypeLabel: string;
  tariffLabel: string;
}

export function updateDismissedShiplifyRecommendations(
  dismissed: string[],
  key: string,
  shouldDismiss: boolean
) {
  const next = new Set(dismissed);

  if (shouldDismiss) {
    next.add(key);
  } else {
    next.delete(key);
  }

  return Array.from(next);
}

/**
 * Builds the Shiplify recommendation rows displayed in the Tai
 * accessorials section.
 *
 * Recommendations are flattened across pickup and dropoff stops,
 * enriched with display metadata, and sorted to match the accessorial
 * catalog order.
 */
function buildShiplifyRecommendations(
  accessorials: LTLAccessorial[],
  lookups: Partial<Record<StopTypes, LTLShiplifyLookupMetadata>>
): ShiplifyRecommendationDisplay[] {
  // Use catalog position as the secondary sort key.
  const catalogIndex = new Map(
    accessorials.map((accessorial, index) => [
      getAccessorialId(accessorial),
      index,
    ])
  );

  // Flatten recommendations from both stops while preserving unmatched
  // codes.
  return (
    SHIPLIFY_STOP_TYPES.flatMap((stopType) => {
      const recommendations = lookups[stopType]?.recommendations ?? [];
      return recommendations.flatMap((recommendation) => {
        const matches = matchShiplifyRecommendations(
          [recommendation],
          accessorials,
          stopType
        );
        return [...matches.autoApply, ...matches.suggestions].map((match) => ({
          ...match,
          stopType,
          category:
            getPrimaryCategory(match.accessorial) ??
            (stopType === StopTypes.Pickup ? 'pickup' : 'delivery'),
        }));
      });
    })
      // Keep original order as stable final tie-breaker.
      .map((match, originalIndex) => ({ ...match, originalIndex }))
      .sort((left, right) => {
        const leftId = left.accessorial
          ? getAccessorialId(left.accessorial)
          : '';
        const rightId = right.accessorial
          ? getAccessorialId(right.accessorial)
          : '';

        // Sort by display category, catalog order, then source order.
        return (
          CATEGORY_ORDER.indexOf(left.category) -
            CATEGORY_ORDER.indexOf(right.category) ||
          (catalogIndex.get(leftId) ?? Number.MAX_SAFE_INTEGER) -
            (catalogIndex.get(rightId) ?? Number.MAX_SAFE_INTEGER) ||
          left.originalIndex - right.originalIndex
        );
      })
  );
}

function getShiplifyRecommendationData(
  match: ShiplifyRecommendationDisplay,
  lookups: Partial<Record<StopTypes, LTLShiplifyLookupMetadata>>
): ShiplifyRecommendationData {
  const { accessorial, code, recommendation, stopType } = match;
  const accuracy =
    recommendation.accuracy ??
    shiplifyConfidenceAccuracy(recommendation.confidence);
  const exempt =
    recommendation.exempt ||
    recommendation.decision === LTLShiplifyDecision.Ignore;
  const locationTypeLabel = (lookups[stopType]?.locationTypes ?? [])
    .map(formatShiplifyLocationType)
    .filter(Boolean)
    .join(', ');

  const recommendationData: ShiplifyRecommendationData = {
    accuracy,
    exempt,
    locationTypeLabel,
    tariffLabel:
      accessorial?.name ??
      formatShiplifyTariffCode(code ?? 'Recommended accessorial'),
  };

  return recommendationData;
}

/** Tai LTL accessorials grouped by pickup, delivery, shipment. */
export function AccessorialsSection({
  formMethods,
  accessorials,
  isLoading = false,
}: AccessorialsSectionProps) {
  const { control, setValue, watch } = formMethods;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'ltl.selectedAccessorials',
  });
  const dismissedShiplifyRecommendations =
    useWatch({
      control,
      name: 'ltl.dismissedShiplifyRecommendations',
      defaultValue: [],
    }) ?? [];

  const selectedAccessorials =
    useWatch({
      control,
      name: 'ltl.selectedAccessorials',
      defaultValue: [],
    }) ?? [];

  const shiplifyLookups =
    useWatch({
      control,
      name: 'ltl.shiplifyLookups',
      defaultValue: {},
    }) ?? {};

  const stops =
    useWatch({
      control,
      name: 'stops',
    }) ?? [];
  const pickupShiplifyAddressKey = getShiplifyAddressKey(
    getShiplifyStop(stops, 0)
  );
  const dropoffShiplifyAddressKey = getShiplifyAddressKey(
    getShiplifyStop(stops, 1)
  );
  const shiplifyAddressKeys = {
    [StopTypes.Pickup]: pickupShiplifyAddressKey,
    [StopTypes.Dropoff]: dropoffShiplifyAddressKey,
  };
  const getShiplifyRecommendationKey = (
    stopType: Undef<StopTypes>,
    identifier: string
  ) =>
    `${stopType ?? 'unknown'}:${stopType ? shiplifyAddressKeys[stopType] : ''}:${identifier}`;

  const items =
    useWatch({
      control,
      name: 'ltl.items',
    }) ?? [];

  const unitSystem =
    useWatch({
      control,
      name: 'ltl.commodity.units',
    }) ?? LTL_UNIT_SYSTEMS.IMPERIAL;

  const requiredOverlengthCode = useMemo(
    () => getTaiOverlengthAccessorialCode(items, unitSystem),
    [items, unitSystem]
  );

  // Keep selected cards in the same order as their source catalog.
  useEffect(() => {
    if (isLoading || accessorials.length === 0) {
      return;
    }

    const reconciled = reconcileTaiOverlengthAccessorials(
      selectedAccessorials,
      accessorials,
      requiredOverlengthCode
    );

    if (reconciled !== selectedAccessorials) {
      setValue('ltl.selectedAccessorials', reconciled, {
        shouldDirty: true,
      });
    }
  }, [
    accessorials,
    isLoading,
    requiredOverlengthCode,
    selectedAccessorials,
    setValue,
  ]);

  // Keep selected accessorials in category/catalog order as the
  // catalog loads or changes.
  useEffect(() => {
    if (isLoading || accessorials.length === 0) {
      return;
    }

    const sorted = sortSelectedAccessorials(selectedAccessorials, accessorials);
    if (
      sorted.some(
        (selection, index) => selection !== selectedAccessorials[index]
      )
    ) {
      replace(sorted);
    }
  }, [accessorials, isLoading, replace, selectedAccessorials]);

  const accessorialsByCategory = useMemo(() => {
    const grouped: Record<LTLAccessorialCategoryTag, LTLAccessorial[]> = {
      pickup: [],
      delivery: [],
      shipment: [],
    };

    accessorials.forEach((accessorial) => {
      (accessorial.categoryTags ?? []).forEach((tag) => {
        if (
          grouped[tag] &&
          !grouped[tag].some(
            (a) => getAccessorialId(a) === getAccessorialId(accessorial)
          )
        ) {
          grouped[tag].push(accessorial);
        }
      });
    });

    return grouped;
  }, [accessorials]);

  const categoryCounts = useMemo(
    () => getCountsByCategory(selectedAccessorials, accessorials),
    [selectedAccessorials, accessorials]
  );

  const categoryOptions = useMemo(() => {
    const options: Record<
      LTLAccessorialCategoryTag,
      Array<{ label: string; value: string }>
    > = {
      pickup: [],
      delivery: [],
      shipment: [],
    };

    CATEGORY_ORDER.forEach((category) => {
      const items = accessorialsByCategory[category];
      options[category] = items.map((accessorial) => ({
        label: accessorial.name,
        value: getAccessorialId(accessorial),
      }));
    });

    return options;
  }, [accessorialsByCategory]);

  function getSelectedIndex(identifier: string): number {
    return selectedAccessorials.findIndex((a: LTLSelectedAccessorial) => {
      const selectedId =
        a.code || (a.externalId != null ? String(a.externalId) : '');
      return selectedId === identifier;
    });
  }

  function getAccessorialDef(identifier: string): Undef<LTLAccessorial> {
    return findAccessorialById(accessorials, identifier);
  }

  const selectedCodesByCategory = useMemo(() => {
    const codes: Record<LTLAccessorialCategoryTag, string[]> = {
      pickup: [],
      delivery: [],
      shipment: [],
    };

    selectedAccessorials.forEach((selected: LTLSelectedAccessorial) => {
      const identifier =
        selected.code ||
        (selected.externalId != null ? String(selected.externalId) : '');

      if (!identifier) {
        return;
      }

      const def = findAccessorialById(accessorials, identifier);
      const category = getPrimaryCategory(def);

      if (category) {
        codes[category].push(identifier);
      }
    });

    return codes;
  }, [selectedAccessorials, accessorials]);

  function handleCategorySelectChange(
    category: LTLAccessorialCategoryTag,
    selectedCodes: string[]
  ) {
    const currentCodes = selectedCodesByCategory[category];

    const removedCodes = currentCodes.filter(
      (code) => !selectedCodes.includes(code)
    );
    const addedCodes = selectedCodes.filter(
      (code) => !currentCodes.includes(code)
    );

    const indicesToRemove = removedCodes
      .map((code) => getSelectedIndex(code))
      .filter((index) => index >= 0)
      .sort((a, b) => b - a);

    const nextDismissedShiplifyRecommendations = indicesToRemove.reduce(
      (dismissed, index) => {
        const selection = selectedAccessorials[index];
        if (selection?.recommendedByShiplify) {
          const key = getShiplifyRecommendationKey(
            selection.recommendedByShiplifyStop,
            selection.code || String(selection.externalId ?? '')
          );

          return updateDismissedShiplifyRecommendations(dismissed, key, true);
        }

        return dismissed;
      },
      dismissedShiplifyRecommendations
    );

    if (
      nextDismissedShiplifyRecommendations.length !==
      dismissedShiplifyRecommendations.length
    ) {
      setValue(
        'ltl.dismissedShiplifyRecommendations',
        nextDismissedShiplifyRecommendations,
        { shouldDirty: true }
      );
    }

    indicesToRemove.forEach((index) => {
      remove(index);
    });

    addedCodes.forEach((identifier) => {
      const accessorial = getAccessorialDef(identifier);
      if (accessorial) {
        const newSelection: LTLSelectedAccessorial = {
          code: accessorial.code || '',
          name: accessorial.name,
          externalId: accessorial.externalId,
        };

        if (
          accessorial.actions &&
          Object.keys(accessorial.actions).length > 0
        ) {
          newSelection.actionValues = {};
          Object.keys(accessorial.actions).forEach((key) => {
            newSelection.actionValues![key] = '';
          });
        }

        append(newSelection);
      }
    });
  }

  function removeAccessorial(identifier: string) {
    const index = getSelectedIndex(identifier);
    if (index >= 0) {
      const selection = selectedAccessorials[index];
      if (selection?.recommendedByShiplify) {
        const key = getShiplifyRecommendationKey(
          selection.recommendedByShiplifyStop,
          identifier
        );

        setValue(
          'ltl.dismissedShiplifyRecommendations',
          updateDismissedShiplifyRecommendations(
            dismissedShiplifyRecommendations,
            key,
            true
          ),
          { shouldDirty: true }
        );
      }

      remove(index);
    }
  }

  const shiplifyRecommendations = useMemo(() => {
    return buildShiplifyRecommendations(accessorials, shiplifyLookups);
  }, [accessorials, shiplifyLookups]);

  // Apply eligible recommendations once while honoring explicit removals.
  useEffect(() => {
    const selectedIds = new Set(
      selectedAccessorials.map(
        (selection) => selection.code || String(selection.externalId ?? '')
      )
    );
    const recommendationsToApply = shiplifyRecommendations.filter(
      (match) =>
        match.recommendation.decision === LTLShiplifyDecision.AutoApply &&
        match.accessorial &&
        !selectedIds.has(getAccessorialId(match.accessorial)) &&
        !dismissedShiplifyRecommendations.includes(
          getShiplifyRecommendationKey(
            match.stopType,
            getAccessorialId(match.accessorial)
          )
        )
    );

    if (recommendationsToApply.length === 0) {
      return;
    }

    const nextSelections = [...selectedAccessorials];
    recommendationsToApply.forEach((match) => {
      if (!match.accessorial) {
        return;
      }

      const identifier = getAccessorialId(match.accessorial);
      if (selectedIds.has(identifier)) {
        return;
      }
      selectedIds.add(identifier);

      const selection: LTLSelectedAccessorial = {
        code: match.accessorial.code,
        name: match.accessorial.name,
        externalId: match.accessorial.externalId,
        recommendedByShiplify: true,
        recommendedByShiplifyStop: match.stopType,
        shiplifyRecommendationCode: match.code,
        shiplifyConfidence: match.recommendation.confidence,
        shiplifyAccuracy: match.recommendation.accuracy,
      };
      if (match.accessorial.actions) {
        selection.actionValues = Object.fromEntries(
          Object.keys(match.accessorial.actions).map((key) => [key, ''])
        );
      }
      nextSelections.push(selection);
    });

    replace(sortSelectedAccessorials(nextSelections, accessorials));
  }, [
    accessorials,
    dropoffShiplifyAddressKey,
    pickupShiplifyAddressKey,
    replace,
    selectedAccessorials,
    dismissedShiplifyRecommendations,
    shiplifyRecommendations,
  ]);

  function addShiplifyRecommendation(match: ShiplifyRecommendationDisplay) {
    if (
      !match.accessorial ||
      match.recommendation.exempt ||
      match.recommendation.decision === LTLShiplifyDecision.Ignore
    ) {
      return;
    }

    const identifier = getAccessorialId(match.accessorial);

    if (getSelectedIndex(identifier) >= 0) {
      return;
    }

    const key = getShiplifyRecommendationKey(match.stopType, identifier);
    setValue(
      'ltl.dismissedShiplifyRecommendations',
      updateDismissedShiplifyRecommendations(
        dismissedShiplifyRecommendations,
        key,
        false
      ),
      { shouldDirty: true }
    );

    const selection: LTLSelectedAccessorial = {
      code: match.accessorial.code,
      name: match.accessorial.name,
      externalId: match.accessorial.externalId,
      recommendedByShiplify: true,
      recommendedByShiplifyStop: match.stopType,
      shiplifyRecommendationCode: match.code,
      shiplifyConfidence: match.recommendation.confidence,
      shiplifyAccuracy: match.recommendation.accuracy,
    };
    if (match.accessorial.actions) {
      selection.actionValues = Object.fromEntries(
        Object.keys(match.accessorial.actions).map((key) => [key, ''])
      );
    }
    append(selection);
  }

  const hasSelectedAccessorials = selectedAccessorials.some(
    (s: LTLSelectedAccessorial) => s?.code || s?.externalId
  );

  return (
    <Flex direction='col' gap='lg' className='w-full'>
      <Typography
        variant='h5'
        className='text-neutral-800 font-semibold whitespace-nowrap'
      >
        Accessorials
      </Typography>

      {isLoading ? (
        <div className='text-sm text-neutral-500 text-center py-4'>
          Loading accessorials...
        </div>
      ) : accessorials.length === 0 ? (
        <div className='text-sm text-neutral-500 text-center py-4'>
          No accessorials available
        </div>
      ) : (
        <Flex direction='col' gap='md' className='w-full'>
          {CATEGORY_ORDER.map((category) => {
            const items = accessorialsByCategory[category];
            if (items.length === 0) {
              return null;
            }
            const config = CATEGORY_CONFIG[category];
            const categoryCount = categoryCounts[category];
            const options = categoryOptions[category];
            const selectedCodes = selectedCodesByCategory[category];

            return (
              <div className='w-full' key={category}>
                <Flex align='center' justify='between'>
                  <div>
                    <Label name='ltl.selectedAccessorials' required={false}>
                      <Flex align='center' gap='xs'>
                        <Typography
                          variant='body-xs'
                          weight='semibold'
                          className='text-neutral-500 uppercase tracking-wide'
                        >
                          {config.label}
                        </Typography>
                        <span
                          aria-hidden='true'
                          className={cn(
                            'block h-0.5 w-10 rounded-full',
                            config.color
                          )}
                        />
                      </Flex>
                    </Label>
                  </div>
                  {categoryCount > 0 && (
                    <span
                      className={cn(
                        'text-xs px-2 py-0.5 rounded-full font-medium',
                        config.badgeBg,
                        config.badgeText
                      )}
                    >
                      {categoryCount}
                    </span>
                  )}
                </Flex>
                <MultiSelect
                  key={`${category}-${accessorials.length}`}
                  placeholder={`Select ${config.label.toLowerCase()} accessorials...`}
                  value={selectedCodes || []}
                  onChange={(codes) =>
                    handleCategorySelectChange(category, codes || [])
                  }
                  options={options}
                  className='w-full'
                  displayMode='count'
                />
              </div>
            );
          })}

          {shiplifyRecommendations.length > 0 && (
            <div
              data-testid='shiplify-recommendations'
              className='order-first w-full rounded-sm border border-neutral-200 bg-neutral-50 px-3 py-2.5'
            >
              <Flex align='center' justify='between' className='mb-2'>
                <Typography
                  variant='body-xs'
                  weight='semibold'
                  className='text-neutral-700'
                >
                  Shiplify recommendations
                </Typography>
                <Badge
                  variant='secondary'
                  className='rounded-full border border-brand-500 bg-brand-50 text-[10px] text-brand-500'
                >
                  {shiplifyRecommendations.length}
                </Badge>
              </Flex>
              <div className='divide-y divide-neutral-200'>
                {shiplifyRecommendations.map((match, index) => {
                  const categoryConfig = CATEGORY_CONFIG[match.category];
                  const identifier = match.accessorial
                    ? getAccessorialId(match.accessorial)
                    : '';
                  const selected = identifier
                    ? getSelectedIndex(identifier) >= 0
                    : false;
                  const { accuracy, exempt, locationTypeLabel, tariffLabel } =
                    getShiplifyRecommendationData(match, shiplifyLookups);
                  return (
                    <Flex
                      key={`${match.stopType}-${match.code ?? index}`}
                      data-testid='shiplify-recommendation'
                      data-category={match.category}
                      align='center'
                      justify='between'
                      gap='sm'
                      className='relative py-2 pl-3 first:pt-0 last:pb-0'
                    >
                      <span
                        aria-hidden='true'
                        className={cn(
                          'absolute inset-y-0 left-0 w-1.5 rounded-full',
                          categoryConfig.color
                        )}
                      />
                      <div className='min-w-0 flex-1'>
                        <Flex
                          align='start'
                          justify='between'
                          gap='sm'
                          className='min-w-0'
                        >
                          <Typography
                            variant='body-xs'
                            weight='medium'
                            className='min-w-0 whitespace-normal break-words text-neutral-800'
                          >
                            {tariffLabel}
                          </Typography>
                          {exempt && (
                            <Badge
                              variant='secondary'
                              className='shrink-0 rounded-full bg-neutral-200 px-1.5 py-0 text-[10px] font-medium text-neutral-600'
                            >
                              Exempt
                            </Badge>
                          )}
                        </Flex>
                        <Grid
                          align='baseline'
                          gapX='sm'
                          className='mt-1 grid-cols-[auto_1fr] gap-y-0.5'
                        >
                          <Typography
                            as='span'
                            variant='body-xxs'
                            textColor='moreMuted'
                          >
                            Tariff
                          </Typography>
                          <Typography
                            as='span'
                            variant='body-xxs'
                            weight='semibold'
                            textColor='brand'
                          >
                            {match.code?.toUpperCase() ?? '—'}
                          </Typography>
                          <Typography
                            as='span'
                            variant='body-xxs'
                            textColor='moreMuted'
                          >
                            Confidence
                          </Typography>
                          <Typography
                            as='span'
                            variant='body-xxs'
                            weight='medium'
                            className='text-neutral-700'
                          >
                            {match.recommendation.confidence}
                          </Typography>
                          {locationTypeLabel && (
                            <>
                              <Typography
                                as='span'
                                variant='body-xxs'
                                textColor='moreMuted'
                              >
                                Location type
                              </Typography>
                              <Typography
                                as='span'
                                variant='body-xxs'
                                weight='medium'
                                className='text-neutral-700'
                              >
                                {locationTypeLabel}
                              </Typography>
                            </>
                          )}
                        </Grid>
                      </div>
                      <div className='ml-auto flex shrink-0 flex-col items-end gap-1'>
                        {accuracy !== undefined && (
                          <Badge
                            variant='secondary'
                            className='shrink-0 rounded-full border border-brand-300 bg-brand-50 px-1.5 py-0 text-[10px] font-semibold text-brand-main'
                          >
                            {accuracy}%
                          </Badge>
                        )}
                        {selected ? (
                          <span className='inline-flex items-center gap-1 text-right text-[10px] font-medium text-success-700'>
                            <Check className='h-3 w-3' /> Added
                          </span>
                        ) : match.accessorial && !exempt ? (
                          <Button
                            type='button'
                            variant='secondary'
                            size='auto'
                            buttonNamePosthog={null}
                            onClick={() => addShiplifyRecommendation(match)}
                            className='h-6 shrink-0 px-2 text-[10px] text-brand-main'
                          >
                            <Plus className='h-3 w-3' /> Add
                          </Button>
                        ) : !selected && !exempt ? (
                          <span className='shrink-0 text-right text-[10px] text-warning-800'>
                            No matching TAI accessorial
                          </span>
                        ) : null}
                      </div>
                    </Flex>
                  );
                })}
              </div>
            </div>
          )}

          {hasSelectedAccessorials && (
            <Flex direction='col' className='w-full'>
              <AnimatePresence initial={false} mode='popLayout'>
                {fields.map((field, index) => {
                  const watched = watch(`ltl.selectedAccessorials.${index}`);

                  let selected = watched;
                  if (!selected) {
                    selected = field as unknown as LTLSelectedAccessorial;
                  }

                  const identifier =
                    selected?.code ||
                    (selected?.externalId != null
                      ? String(selected.externalId)
                      : '');
                  if (!identifier) {
                    return null;
                  }

                  const def = getAccessorialDef(identifier);
                  const category = getPrimaryCategory(def);
                  const categoryConfig = category
                    ? CATEGORY_CONFIG[category]
                    : undefined;

                  return (
                    <AccessorialCard
                      key={field.id}
                      index={index}
                      selected={selected}
                      def={def}
                      categoryConfig={categoryConfig}
                      control={control}
                      onRemove={() => removeAccessorial(identifier)}
                    />
                  );
                })}
              </AnimatePresence>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
}
